const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const PBKDF2_ITERATIONS = 100_000;

// const SECRET_KEY = import.meta.env.VITE_TOKEN_DECRYPT_SECRET;
const SECRET_KEY = process.env.REACT_APP_TOKEN_DECRYPT_SECRET || '';

export async function encryptToken(
  token: string,
): Promise<string> {
  if(!SECRET_KEY) {
    throw new Error('SECRET_KEY is not set');
  }
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveKey(SECRET_KEY, salt);

  const encoder = new TextEncoder();
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: ALGORITHM,
      iv,
    },
    key,
    encoder.encode(token)
  );

  const combined = new Uint8Array(
    salt.length + iv.length + ciphertext.byteLength
  );
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(ciphertext), salt.length + iv.length);

  return btoa(String.fromCharCode(...Array.from(combined)));
}


async function deriveKey(secret: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}


export async function decryptToken(
  encryptedValue: string,
): Promise<string> {
  if(!SECRET_KEY) {
    throw new Error('SECRET_KEY is not set');
  }
  const combined = Uint8Array.from(atob(encryptedValue), (c) =>
    c.charCodeAt(0)
  );

  const salt = combined.subarray(0, SALT_LENGTH);
  const iv = combined.subarray(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const ciphertext = combined.subarray(SALT_LENGTH + IV_LENGTH);

  const key = await deriveKey(SECRET_KEY, salt);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: ALGORITHM,
      iv,
    },
    key,
    ciphertext
  );

  return new TextDecoder().decode(decrypted);
}
